/**
 * sections.ts — Blocos de seção genéricos do Studio
 *
 * Blocos de uso geral que atendem às categorias clássicas de uma página/site
 * (capa, título, texto com imagem, galeria, vídeo, números, logos, preços,
 * perguntas frequentes, etapas, equipe, chamada para ação, prazo, contato,
 * menu e rodapé).
 *
 * Convenção de conteúdo em lista: uma linha por item e "|" separando as partes,
 * exatamente como já acontece em `page.features` e `page.testimonials`. Isso
 * mantém o painel de propriedades gerado automaticamente pelo catálogo, sem
 * editores especiais por bloco.
 *
 * Cada bloco registrado aqui TEM de ter componente web
 * (`src/lib/studio/render/web/sectionBlocks.tsx`) e compilador HTML
 * (`supabase/functions/_shared/studio/htmlRenderer.ts`).
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, construtor de blocos prontos)
 */

import { registerBlocks } from '../blockRegistry';
import type { StudioOutput } from '../types';

/** Blocos que sabem se desenhar em qualquer forma de entrega. */
const TODAS: StudioOutput[] = ['web', 'pdf', 'email'];
/** Blocos que só fazem sentido em página no navegador. */
const SO_WEB: StudioOutput[] = ['web'];

/** Opções de alinhamento reaproveitadas por vários blocos. */
const ALINHAMENTO = [
  { value: 'left', label: 'Esquerda' },
  { value: 'center', label: 'Centro' },
];

/** Opções de quantidade de colunas. */
const COLUNAS = [
  { value: '2', label: '2 colunas' },
  { value: '3', label: '3 colunas' },
  { value: '4', label: '4 colunas' },
];

registerBlocks([
  // ---------------------------------------------------------------- Título
  {
    key: 'page.heading',
    label: 'Título de seção',
    family: 'content',
    icon: 'heading',
    outputs: TODAS,
    props: [
      { key: 'eyebrow', label: 'Chapéu (texto pequeno acima)', type: 'text' },
      { key: 'title', label: 'Título', type: 'text', defaultValue: 'Título da seção' },
      { key: 'subtitle', label: 'Subtítulo', type: 'text' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'center', options: ALINHAMENTO },
      { key: 'accentColor', label: 'Cor de destaque', type: 'color' },
    ],
  },

  // -------------------------------------------------------- Texto e imagem
  {
    key: 'page.split',
    label: 'Texto com imagem',
    family: 'content',
    icon: 'columns-2',
    outputs: TODAS,
    props: [
      { key: 'eyebrow', label: 'Chapéu', type: 'text' },
      { key: 'title', label: 'Título', type: 'text', defaultValue: 'Um benefício em destaque' },
      { key: 'text', label: 'Texto', type: 'richtext', defaultValue: 'Explique aqui, em poucas linhas, o que o cliente ganha.' },
      { key: 'imageUrl', label: 'Imagem', type: 'image' },
      {
        key: 'imagePosition',
        label: 'Posição da imagem',
        type: 'select',
        defaultValue: 'right',
        options: [
          { value: 'left', label: 'À esquerda' },
          { value: 'right', label: 'À direita' },
        ],
      },
      { key: 'ctaLabel', label: 'Texto do botão', type: 'text' },
      { key: 'ctaHref', label: 'Destino do botão', type: 'text', defaultValue: '#formulario' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
    ],
  },

  // ------------------------------------------------------ Imagens e galeria
  {
    key: 'page.gallery',
    label: 'Galeria de imagens',
    family: 'content',
    icon: 'images',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      {
        key: 'items',
        label: 'Imagens (uma por linha: endereço|legenda)',
        type: 'richtext',
        defaultValue: '',
        help: 'Exemplo: https://.../foto.jpg|Nossa equipe',
        syntax: 'Uma imagem por linha: endereco|legenda. A legenda é opcional. O endereço aceita acervo://<chave>, https://... ou data:.',
        example: 'acervo://capa.equipe|Nossa equipe\nacervo://capa.escritorio|Sede em São Paulo',
      },
      { key: 'columns', label: 'Colunas', type: 'select', defaultValue: '3', options: COLUNAS },
      {
        key: 'ratio',
        label: 'Formato',
        type: 'select',
        defaultValue: 'landscape',
        options: [
          { value: 'landscape', label: 'Retangular' },
          { value: 'square', label: 'Quadrado' },
          { value: 'portrait', label: 'Vertical' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------- Vídeo
  {
    key: 'page.video',
    label: 'Vídeo',
    family: 'content',
    icon: 'play',
    outputs: SO_WEB,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      { key: 'videoUrl', label: 'Endereço do vídeo (incorporação)', type: 'text', help: 'Use o endereço de incorporação, ex.: https://www.youtube.com/embed/ID' },
      { key: 'posterUrl', label: 'Imagem de capa', type: 'image' },
      { key: 'caption', label: 'Legenda', type: 'text' },
    ],
  },

  // ------------------------------------------------- Números e resultados
  {
    key: 'page.stats',
    label: 'Números e resultados',
    family: 'content',
    icon: 'trending-up',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text' },
      {
        key: 'items',
        label: 'Números (um por linha: valor|descrição)',
        type: 'richtext',
        defaultValue: '+12 anos|de mercado\n+3.500|clientes atendidos\n98%|de satisfação',
        syntax: 'Um número por linha: valor|descricao.',
        example: '+12 anos|de mercado\n+3.500|clientes atendidos\n98%|de satisfação',
      },
      { key: 'columns', label: 'Colunas', type: 'select', defaultValue: '3', options: COLUNAS },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
    ],
  },

  // ------------------------------------------------------ Logos/parceiros
  {
    key: 'page.logos',
    label: 'Logos de parceiros',
    family: 'content',
    icon: 'building-2',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Empresas que confiam' },
      {
        key: 'items',
        label: 'Logos (um por linha: endereço da imagem|nome)',
        type: 'richtext',
        defaultValue: '',
        syntax: 'Um logo por linha: endereco_da_imagem|nome. O nome é usado como descrição da imagem.',
        example: 'https://exemplo.com/cliente-a.svg|Cliente A\nhttps://exemplo.com/cliente-b.svg|Cliente B',
      },
      { key: 'grayscale', label: 'Exibir em tons de cinza', type: 'boolean', defaultValue: true },
    ],
  },

  // ------------------------------------------------------- Preços e planos
  {
    key: 'page.pricing',
    label: 'Preços e planos',
    family: 'content',
    icon: 'badge-dollar-sign',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Escolha o seu plano' },
      {
        key: 'items',
        label: 'Planos (um por linha: nome|preço|período|itens separados por ;|destaque|botão|destino)',
        type: 'richtext',
        syntax:
          'Um plano por linha, sete campos separados por |: nome|preco|periodo|itens|destaque|texto_do_botao|destino. ' +
          'Os itens do plano são separados por ; e o destaque aceita apenas sim ou não.',
        example:
          'Essencial|R$ 149|por mês|1 usuário;Suporte por e-mail|não|Assinar|#formulario\n' +
          'Profissional|R$ 299|por mês|5 usuários;Suporte prioritário;Relatórios|sim|Assinar|#formulario',
        defaultValue:
          'Essencial|R$ 149|por mês|1 usuário;Suporte por e-mail|não|Assinar|#formulario\nProfissional|R$ 299|por mês|5 usuários;Suporte prioritário;Relatórios|sim|Assinar|#formulario',
      },
      { key: 'columns', label: 'Colunas', type: 'select', defaultValue: '3', options: COLUNAS },
      { key: 'note', label: 'Observação abaixo dos planos', type: 'text' },
    ],
  },

  // ------------------------------------------------- Perguntas frequentes
  {
    key: 'page.faq',
    label: 'Perguntas frequentes',
    family: 'content',
    icon: 'help-circle',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Perguntas frequentes' },
      {
        key: 'items',
        label: 'Perguntas (uma por linha: pergunta|resposta)',
        type: 'richtext',
        syntax: 'Uma pergunta por linha: pergunta|resposta.',
        example: 'Como funciona a contratação?|Você envia os dados e a equipe cuida do resto.\nTem fidelidade?|Não, o contrato é mensal.',
        defaultValue: 'Como funciona a contratação?|É simples: você envia os dados e a equipe cuida do resto.',
      },
      {
        key: 'columns',
        label: 'Colunas',
        type: 'select',
        defaultValue: '1',
        options: [
          { value: '1', label: '1 coluna' },
          { value: '2', label: '2 colunas' },
        ],
      },
    ],
  },

  // --------------------------------------------------- Etapas / passo a passo
  {
    key: 'page.steps',
    label: 'Etapas / Como funciona',
    family: 'content',
    icon: 'list-ordered',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Como funciona' },
      {
        key: 'items',
        label: 'Etapas (uma por linha: título|descrição)',
        type: 'richtext',
        syntax: 'Uma etapa por linha, na ordem em que aparecem: titulo|descricao.',
        example: 'Solicite|Preencha o formulário em 1 minuto\nReceba|Enviamos a proposta no mesmo dia\nContrate|Assinatura digital, sem papel',
        defaultValue: 'Solicite|Preencha o formulário em 1 minuto\nReceba|Enviamos a proposta no mesmo dia\nContrate|Assinatura digital, sem papel',
      },
      {
        key: 'layout',
        label: 'Disposição',
        type: 'select',
        defaultValue: 'horizontal',
        options: [
          { value: 'horizontal', label: 'Lado a lado' },
          { value: 'vertical', label: 'Uma abaixo da outra' },
        ],
      },
    ],
  },

  // ----------------------------------------------------------------- Equipe
  {
    key: 'page.team',
    label: 'Equipe',
    family: 'content',
    icon: 'users',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Quem cuida de você' },
      {
        key: 'items',
        label: 'Pessoas (uma por linha: foto|nome|cargo)',
        type: 'richtext',
        syntax: 'Uma pessoa por linha: foto|nome|cargo. Deixe a foto vazia para usar as iniciais do nome.',
        example: 'acervo://capa.retrato|Ana Martins|Diretora de operações\n|Carlos Lima|Gerente comercial',
        defaultValue: '|Ana Martins|Diretora de operações',
      },
      { key: 'columns', label: 'Colunas', type: 'select', defaultValue: '4', options: COLUNAS },
    ],
  },

  // --------------------------------------------------- Chamada para ação
  {
    key: 'page.cta',
    label: 'Chamada para ação',
    family: 'content',
    icon: 'megaphone',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título', type: 'text', defaultValue: 'Pronto para começar?' },
      { key: 'subtitle', label: 'Texto de apoio', type: 'text' },
      { key: 'buttonLabel', label: 'Texto do botão', type: 'text', defaultValue: 'Falar com um especialista' },
      { key: 'buttonHref', label: 'Destino do botão', type: 'text', defaultValue: '#formulario' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color', defaultValue: '#111827' },
      { key: 'textColor', label: 'Cor do texto', type: 'color', defaultValue: '#ffffff' },
      { key: 'align', label: 'Alinhamento', type: 'select', defaultValue: 'center', options: ALINHAMENTO },
    ],
  },

  // ---------------------------------------------------------------- Prazo
  {
    key: 'page.deadline',
    label: 'Prazo / validade',
    family: 'content',
    icon: 'timer',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título', type: 'text', defaultValue: 'Condição válida até 30/12' },
      { key: 'note', label: 'Observação', type: 'text', defaultValue: 'Depois desta data os valores podem mudar.' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color', defaultValue: '#fef3c7' },
      { key: 'textColor', label: 'Cor do texto', type: 'color', defaultValue: '#92400e' },
    ],
  },

  // -------------------------------------------------------------- Contato
  {
    key: 'page.contact',
    label: 'Contato e endereço',
    family: 'content',
    icon: 'map-pin',
    outputs: TODAS,
    props: [
      { key: 'title', label: 'Título do bloco', type: 'text', defaultValue: 'Fale com a gente' },
      {
        key: 'items',
        label: 'Informações (uma por linha: rótulo|valor)',
        type: 'richtext',
        syntax: 'Uma informação por linha: rotulo|valor.',
        example: 'Telefone|(11) 4000-0000\nE-mail|contato@empresa.com.br',
        defaultValue: 'Telefone|(11) 4000-0000\nE-mail|contato@empresa.com.br\nEndereço|Av. Paulista, 1000 — São Paulo/SP',
      },
      { key: 'columns', label: 'Colunas', type: 'select', defaultValue: '3', options: COLUNAS },
    ],
  },

  // ------------------------------------------------------------ Menu topo
  {
    key: 'page.nav',
    label: 'Menu do topo',
    family: 'layout',
    icon: 'panel-top',
    outputs: SO_WEB,
    props: [
      { key: 'brand', label: 'Nome da marca', type: 'text', defaultValue: 'Sua Empresa' },
      { key: 'logoUrl', label: 'Logo', type: 'image' },
      {
        key: 'links',
        label: 'Links (um por linha: rótulo|destino)',
        type: 'richtext',
        syntax: 'Um link por linha: rotulo|destino. O destino pode ser âncora (#secao) ou endereço completo.',
        example: 'Benefícios|#beneficios\nPlanos|#planos\nContato|#formulario',
        defaultValue: 'Benefícios|#beneficios\nPlanos|#planos\nContato|#formulario',
      },
      { key: 'ctaLabel', label: 'Texto do botão', type: 'text', defaultValue: 'Fale conosco' },
      { key: 'ctaHref', label: 'Destino do botão', type: 'text', defaultValue: '#formulario' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
    ],
  },

  // --------------------------------------------------------------- Rodapé
  {
    key: 'page.footer',
    label: 'Rodapé',
    family: 'layout',
    icon: 'panel-bottom',
    outputs: TODAS,
    props: [
      { key: 'brand', label: 'Nome da marca', type: 'text', defaultValue: 'Sua Empresa' },
      { key: 'text', label: 'Texto', type: 'text', defaultValue: 'Soluções sob medida para o seu negócio.' },
      {
        key: 'links',
        label: 'Links (um por linha: rótulo|destino)',
        type: 'richtext',
        syntax: 'Um link por linha: rotulo|destino.',
        example: 'Sobre|#sobre\nPolítica de privacidade|https://exemplo.com/privacidade',
        defaultValue: '',
      },
      { key: 'legal', label: 'Aviso legal', type: 'text', defaultValue: '© Sua Empresa. Todos os direitos reservados.' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
      { key: 'textColor', label: 'Cor do texto', type: 'color' },
    ],
  },

  // ---------------------------------------------------------- Faixa de aviso
  {
    key: 'page.banner',
    label: 'Faixa de aviso',
    family: 'layout',
    icon: 'flag',
    outputs: TODAS,
    props: [
      { key: 'text', label: 'Texto', type: 'text', defaultValue: 'Frete grátis para todo o Brasil nesta semana' },
      { key: 'linkLabel', label: 'Texto do link', type: 'text' },
      { key: 'linkHref', label: 'Destino do link', type: 'text', defaultValue: '#formulario' },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color', defaultValue: '#111827' },
      { key: 'textColor', label: 'Cor do texto', type: 'color', defaultValue: '#ffffff' },
    ],
  },
]);
